import { cva } from "class-variance-authority";

import { SafeHtml } from "~/hec/components/atoms/safe-html/safe-html";
import { cn } from "~/lib/ui";

import type { ClaimCardProps } from "./types";

/**
 * Molecule: the card shell of one Perks claim — a port of the SFRA
 * `mainBanner` + `color-combination-N` pair the Insiders page is built from.
 *
 * Colours are per site through the `.FSASTORE` / `.HSASTORE` html classes
 * (CLAUDE.md multi-site theming), using the arbitrary-variant form so the
 * mapping stays local to this component instead of adding semantic tokens to
 * `app.css` for a single block.
 */
const cardVariants = cva(
  "flex h-full w-full flex-col rounded-[8px] px-5 pb-12 pt-10",
  {
    variants: {
      theme: {
        light:
          "text-blue-03 [.FSASTORE_&]:bg-blue-01 [.HSASTORE_&]:bg-indigo-01",
        dark: "bg-blue-04 [.FSASTORE_&]:text-[hsl(var(--color-peach-01))] [.HSASTORE_&]:text-indigo-01",
      },
    },
    defaultVariants: { theme: "light" },
  },
);

/** SFRA `.eyebrow-large`: 14/22 → 16/24 from 768px, 0.05em tracking, site accent colour. */
const eyebrowVariants = cva(
  "eyebrow-lg mb-2.5 min-[768px]:mb-3 min-[768px]:text-base min-[768px]:leading-6 min-[768px]:tracking-widest",
  {
    variants: {
      theme: {
        light: "[.FSASTORE_&]:text-blue-02 [.HSASTORE_&]:text-indigo-04",
        dark: "text-inherit",
      },
    },
  },
);

/** SFRA 3-Up heading: 18/26 → 32/38 from 992px → 40/48 from 1200px, bold, site heading face. */
const headingVariants = cva(
  "mb-[5px] font-heading text-lg font-bold leading-6.5 min-[992px]:text-4xl min-[992px]:leading-[38px] min-[1200px]:text-[40px] min-[1200px]:leading-[48px]",
  {
    variants: {
      theme: {
        light: "text-blue-04",
        dark: "text-inherit",
      },
    },
  },
);

export function ClaimCard({
  eyebrow,
  heading,
  bodyHtml,
  theme = "light",
  children,
  cta,
  message,
  className,
  ...rest
}: ClaimCardProps) {
  return (
    <div className={cn(cardVariants({ theme }), className)} {...rest}>
      <div className="mx-auto flex h-full w-full max-w-[448px] flex-col items-center text-center">
        {eyebrow ? (
          <p className={eyebrowVariants({ theme })}>{eyebrow}</p>
        ) : null}
        <h2 className={headingVariants({ theme })}>{heading}</h2>
        {bodyHtml ? (
          <SafeHtml
            html={bodyHtml}
            className="mb-4 font-grotesque text-[17px] font-light leading-[1.47] [&_p]:inline"
          />
        ) : null}
        <div className="mt-auto flex w-full flex-col pt-2">
          {children}
          {cta}
        </div>
        {message}
      </div>
    </div>
  );
}
